import { useEffect, useState } from "react";
import { CalendarDays, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@workspace/ui/components/ui/popover";
import { DateRangePopup } from "@/components/ui/date-range-popup";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { formatSearchRange } from "@/lib/search-date-label";

export interface DateRangePopoverProps {
  dateFrom: string;
  dateTo: string;
  onChange: (from: string | undefined, to: string | undefined) => void;
}

export function DateRangePopover({ dateFrom, dateTo, onChange }: DateRangePopoverProps) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);

  // The two-month checkout layout overflows below ~640px even with
  // max-w-[96vw] because the day cells have a fixed pixel width. The
  // search-results sidebar lives on the left edge of a phone viewport,
  // so collapse to a single month on narrow screens to keep the popover
  // usable. Homepage uses the same component but renders in a wider
  // centered popover and doesn't need the fallback.
  const [monthsToShow, setMonthsToShow] = useState<1 | 2>(() =>
    typeof window !== "undefined" && window.matchMedia("(min-width: 640px)").matches ? 2 : 1,
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(min-width: 640px)");
    const handler = () => setMonthsToShow(mq.matches ? 2 : 1);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  const placeholder = t("search.filter_labels.add_dates") || "Add dates";
  const label = formatSearchRange(dateFrom, dateTo, i18n.language) || placeholder;
  const hasSelection = Boolean(dateFrom || dateTo);

  const clearAll = () => {
    onChange(undefined, undefined);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={placeholder}
          className={cn(
        "cursor-pointer relative flex w-full items-center rounded-xl border border-input bg-background py-2 pl-9 pr-3 text-left text-sm transition-colors",
            "hover:border-brand focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
          )}
        >
          <CalendarDays className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <span className={hasSelection ? "text-foreground" : "text-muted-foreground"}>{label}</span>
          {hasSelection && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                clearAll();
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.stopPropagation();
                  clearAll();
                }
              }}
              className="ml-auto rounded-full p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
              aria-label={t("common.clear_all") || "Clear"}
            >
              <X className="h-3.5 w-3.5" />
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto overflow-hidden rounded-3xl border border-border/70 bg-white p-0 shadow-[0_24px_60px_-20px_rgba(15,23,42,0.25),0_8px_24px_-12px_rgba(15,23,42,0.15)]"
        align="start"
        sideOffset={10}
      >
        <DateRangePopup
          searchLabels
          selectedFrom={dateFrom || ""}
          selectedTo={dateTo || ""}
          onChange={(from, to) => {
            onChange(from || undefined, to || undefined);
          }}
          onClose={() => setOpen(false)}
          monthsToShow={monthsToShow}
          emptyTitle={t("dateRange.select_dates")}
          emptySubtitle={t("dateRange.pickup_and_dropoff")}
          doneLabel={t("common.done") || "Done"}
          clearLabel={t("common.clear") || "Clear"}
        />
      </PopoverContent>
    </Popover>
  );
}

export default DateRangePopover;
